/**
 * admin GraphQL 用の urql クライアント。
 *
 * - トークンは毎回 AdminSession から取り直す（`fetchOptions` を関数で渡すと
 *   キャッシュされた値を使い回してしまうので、authExchange で都度付ける）
 * - 401（HTTP）と `extensions.code === "UNAUTHENTICATED"`（GraphQL）の両方を
 *   同じ扱いにする。前者はミドルウェアが弾いた場合、後者はリゾルバの手前の
 *   ポリシー層が弾いた場合で、画面から見ればどちらも「ログインし直し」である
 * - キャッシュは document cache（urql 既定）。正規化キャッシュは、一覧と詳細で
 *   取る面が違う（一覧は aio の要約だけ）ため、部分的な上書きが起きやすい。
 *   書き込み後は対象の operation を明示的に再取得する流儀で揃える
 * - **既定の requestPolicy は `cache-and-network`**（urql の既定 `cache-first` を落とす）。
 *   document cache は「応答に入っていた型名」で無効化を決めるので、**空で返った
 *   一覧はどの書き込みでも無効にならない**（応答に型名が 1 つも入らないため）。
 *   画面をまたいで書いたものが、先に開いてあった一覧に出てこない不具合は
 *   これが素地で、#136・#150・配信の提案と 3 回繰り返している。
 *   `cache-and-network` なら、キャッシュを即座に見せたうえで必ず取り直すので、
 *   「無効化を書き忘れると静かに古い値が出る」形そのものが無くなる。
 *   往復は 1 画面あたり 1 回増えるが、この面は社内の管理画面で、
 *   すでに大半の問い合わせが `network-only` を明示している
 * - **問い合わせは必ず POST で送る**。urql の既定（`within-url-limit`）は
 *   収まるかぎり GET にするので、query の全文と変数が URL に載る。URL は
 *   CloudFront と API Gateway のアクセスログに丸ごと残るため、検索語のような
 *   利用者の入力まで保管期間ぶん残ってしまう
 */

import {
  Client,
  cacheExchange,
  fetchExchange,
  mapExchange,
  type Exchange,
} from "urql";
import { authExchange } from "@urql/exchange-auth";

import type { AdminSession } from "../auth/session";

export const UNAUTHENTICATED_CODE = "UNAUTHENTICATED";

export function createAdminClient(input: {
  session: AdminSession;
  /** 開発・テストで API の代わりに差し込む exchange（本番ビルドには入らない） */
  mockExchange?: Exchange;
}): Client {
  const { session, mockExchange } = input;

  const auth = authExchange(async (utils) => {
    return {
      addAuthToOperation(operation) {
        const token = session.tokens.accessToken();
        if (token === null) {
          return operation;
        }
        return utils.appendHeaders(operation, {
          Authorization: `Bearer ${token}`,
        });
      },
      didAuthError(error) {
        return (
          error.response?.status === 401 ||
          error.graphQLErrors.some(
            (graphQLError) =>
              graphQLError.extensions?.["code"] === UNAUTHENTICATED_CODE,
          )
        );
      },
      async refreshAuth() {
        // 取り直せればトークンが入れ替わり、駄目なら session 側が
        // unauthenticated へ落としてログイン画面が出る
        await session.accessTokenForRequest();
      },
      willAuthError() {
        return session.tokens.isExpired();
      },
    };
  });

  // 認証エラーが authExchange の再試行でも解消しなかったときに、最後にもう一度
  // 状態を落とす。ここが無いと画面がエラー表示のまま固まる
  const unauthenticatedGuard = mapExchange({
    onError(error) {
      const unauthenticated =
        error.response?.status === 401 ||
        error.graphQLErrors.some(
          (graphQLError) =>
            graphQLError.extensions?.["code"] === UNAUTHENTICATED_CODE,
        );
      if (unauthenticated) {
        session.handleUnauthenticated();
      }
    },
  });

  return new Client({
    url: session.config.graphqlEndpoint,
    // 既定の 'within-url-limit' を明示的に落とす（上のコメントの理由）
    preferGetMethod: false,
    // 既定の 'cache-first' を落とす（上のコメントの理由）
    requestPolicy: "cache-and-network",
    exchanges: [
      cacheExchange,
      auth,
      unauthenticatedGuard,
      mockExchange ?? fetchExchange,
    ],
  });
}
